import { useEffect } from 'react'
import { captureException } from '@sentry/nextjs'

import { flushEvents } from '~/behavior-tracking/utility/view-event-logs.utility'
import { useAppDispatch } from '~/redux/hooks'
import { curriculumUserStatApi } from '~/curriculum/apis/get-curriculum-user-stats.api'
import { homApi } from '~/redux/apis/hom'
import { BOOTCAMP_TYPE } from '~/redux/constants/tags.const'
import { invalidateStreakIfNeeded } from '~/user-stats/utility/streak.utils'

const AUTO_FLUSH_INTERVAL = 20_000
const INITIAL_FLUSH_DELAY = 6_000

function useAfterFlushEventCallback(): (data: {
  eventsRelatedToVCC: boolean
  eventsRelatedToCurriculum: boolean
  eventsRelatedToVCCVideo: boolean
}) => void {
  const dispatch = useAppDispatch()

  const callback = (data: {
    eventsRelatedToVCC: boolean
    eventsRelatedToCurriculum: boolean
    eventsRelatedToVCCVideo: boolean
  }): void => {
    if (data?.eventsRelatedToCurriculum) {
      dispatch(curriculumUserStatApi.util.invalidateTags(['CurriculumUserStats']))
    }
    if (data?.eventsRelatedToVCCVideo) {
      dispatch(homApi.util.invalidateTags([BOOTCAMP_TYPE.BOOTCAMP_VIDEO_WATCHED]))
    }
  }

  return callback
}

export function useEventBufferFlush(userId?: number | null): void {
  const dispatch = useAppDispatch()

  const callbackAfterFlushEvents = useAfterFlushEventCallback()

  useEffect(() => {
    if (userId == null) {
      return
    }

    // Streak invalidation used to hang off the /v1/process-events POST in analyticsBQ.ts, which made
    // day-rollover streak refresh depend on a telemetry request resolving — and stop entirely once
    // that feed is gated off. This timer is a better carrier: it already runs for logged-in users
    // only, and invalidateStreakIfNeeded self-guards on the day boundary, so it is a no-op on almost
    // every tick. Called here rather than in the .then below on purpose — flushEvents can resolve
    // undefined and submitEvents throws on a failed POST, so the .then path is skipped exactly when
    // the network is unhappy.
    const initialFlushTimeoutId = setTimeout(() => {
      invalidateStreakIfNeeded()
      flushEvents(userId)
        .then(callbackAfterFlushEvents)
        .catch((err) => {
          captureException(err, { tags: { feature: 'view-tracking', action: 'flush-events' } })
          console.error('Failed to flush activity logs', err)
        })
    }, INITIAL_FLUSH_DELAY)

    const periodicFlushIntervalId = setInterval(() => {
      invalidateStreakIfNeeded()
      flushEvents(userId)
        .then(callbackAfterFlushEvents)
        .catch((err) => {
          captureException(err, { tags: { feature: 'view-tracking', action: 'flush-events' } })
          console.error('Failed to flush activity logs', err)
        })
    }, AUTO_FLUSH_INTERVAL)

    return () => {
      clearTimeout(initialFlushTimeoutId)
      clearInterval(periodicFlushIntervalId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, userId])
}
